<template>
  <main class="sms-recharge" aria-labelledby="recharge-title">
    <header class="recharge-header">
      <div class="eyebrow">SMS balance</div>
      <h1 id="recharge-title">Recharge SMS Balance</h1>
      <p>Top up the prepaid unit pool shared across the whole platform. This page is not linked from the sidebar — bookmark the URL if you need it again.</p>
    </header>

    <section class="balance-panel" aria-label="Current balance">
      <div class="balance-grid">
        <div class="balance-cell">
          <span>Remaining</span>
          <strong>{{ formatNumber(quota.remainingUnits) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Purchased</span>
          <strong>{{ formatNumber(quota.totalPurchased) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Used</span>
          <strong>{{ formatNumber(quota.totalUsed) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Recharged</span>
          <strong>₹{{ formatNumber(quota.totalAmountRecharged) }}</strong>
        </div>
        <div class="balance-cell">
          <span>Last Recharge</span>
          <strong v-if="quota.lastRecharge">₹{{ formatNumber(quota.lastRecharge.amount) }}</strong>
          <strong v-else>—</strong>
          <small v-if="quota.lastRecharge">{{ formatDateTime(quota.lastRecharge.createdAt) }} · +{{ formatNumber(quota.lastRecharge.unitsAdded) }} units</small>
          <small v-else>No recharge yet</small>
        </div>
      </div>
    </section>

    <section class="recharge-panel" aria-label="Recharge form">
      <h2>Add funds</h2>
      <form class="recharge-form" @submit.prevent="submitRecharge">
        <div class="field">
          <label for="recharge-amount">Amount (₹)</label>
          <input id="recharge-amount" v-model.number="rechargeForm.amount" type="number" min="1" step="0.01" required placeholder="e.g. 500" />
          <small class="field-hint" v-if="estimatedUnits > 0">≈ {{ formatNumber(estimatedUnits) }} units at ₹{{ quota.unitCost }} / unit</small>
        </div>
        <div class="field">
          <label for="recharge-remark">Remark (optional)</label>
          <input id="recharge-remark" v-model.trim="rechargeForm.remark" type="text" placeholder="e.g. Monthly top-up" />
        </div>
        <div class="recharge-actions">
          <button type="submit" class="btn-ledger btn-apply" :disabled="rechargeSubmitting">
            <span v-if="rechargeSubmitting" class="button-spinner" aria-hidden="true"></span>
            {{ rechargeSubmitting ? 'Recharging' : 'Recharge' }}
          </button>
        </div>
      </form>
    </section>

    <section class="history-panel" aria-label="Recharge history">
      <div class="history-heading">
        <h2>Recharge history</h2>
        <p v-if="!historyLoading && !historyError">{{ historyRangeText }}</p>
      </div>

      <div v-if="historyLoading" class="state-panel" role="status">
        <span class="ledger-loader" aria-hidden="true"></span><strong>Loading recharge history</strong>
      </div>
      <div v-else-if="historyError" class="state-panel state-error" role="alert">
        <strong>Couldn't load recharge history</strong><p>{{ historyError }}</p>
        <button type="button" class="btn-ledger btn-apply" @click="fetchHistory">Try again</button>
      </div>
      <div v-else-if="historyRows.length === 0" class="state-panel">
        <strong>No recharges yet</strong><p>Recharges you add above will show up here.</p>
      </div>
      <div v-else class="table-scroll">
        <table>
          <thead><tr><th>Date &amp; Time</th><th class="number-cell">Amount</th><th class="number-cell">Units Added</th><th>Remark</th></tr></thead>
          <tbody>
            <tr v-for="row in historyRows" :key="row.id">
              <td>{{ formatDateTime(row.createdAt) }}</td>
              <td class="number-cell">₹{{ formatNumber(row.amount) }}</td>
              <td class="number-cell">+{{ formatNumber(row.unitsAdded) }}</td>
              <td>{{ row.remark || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="!historyLoading && !historyError && historyTotal > 0" class="pagination-bar">
        <span>{{ historyRangeText }}</span>
        <nav aria-label="Recharge history pages">
          <button type="button" :disabled="historyPage <= 1" aria-label="Previous page" @click="goToHistoryPage(historyPage - 1)">‹</button>
          <span>Page <strong>{{ historyPage }}</strong> of {{ historyTotalPages }}</span>
          <button type="button" :disabled="historyPage >= historyTotalPages" aria-label="Next page" @click="goToHistoryPage(historyPage + 1)">›</button>
        </nav>
      </footer>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import store from '../store'

export default {
  name: 'SmsRecharge',
  data () {
    return {
      quotaLoading: false,
      quota: { remainingUnits: 0, totalPurchased: 0, totalUsed: 0, unitCost: 0.354, totalAmountRecharged: 0, lastRecharge: null },
      rechargeSubmitting: false,
      rechargeForm: { amount: null, remark: '' },
      historyLoading: false,
      historyError: '',
      historyRows: [],
      historyPage: 1,
      historyLimit: 10,
      historyTotal: 0,
      historyTotalPages: 1
    }
  },
  computed: {
    estimatedUnits () {
      const amount = Number(this.rechargeForm.amount)
      const unitCost = Number(this.quota.unitCost) || 0.354
      return Number.isFinite(amount) && amount > 0 ? Math.floor(amount / unitCost) : 0
    },
    historyRangeText () {
      if (!this.historyTotal) return '0 recharges'
      const start = ((this.historyPage - 1) * this.historyLimit) + 1
      const end = Math.min(this.historyPage * this.historyLimit, this.historyTotal)
      return `${this.formatNumber(start)}–${this.formatNumber(end)} of ${this.formatNumber(this.historyTotal)} recharges`
    }
  },
  mounted () { this.fetchQuota(); this.fetchHistory() },
  methods: {
    notify (type, title, text) { this.$notify({ group: 'notification', type, title, text }) },
    numberValue (value, fallback) { return Number.isFinite(Number(value)) ? Number(value) : fallback },
    formatNumber (value) { return new Intl.NumberFormat().format(this.numberValue(value, 0)) },
    formatDateTime (value) {
      const date = value ? new Date(value) : null
      return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '—'
    },
    errorMessage (err) {
      return (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Please check your connection and try again.'
    },
    normalizeQuota (data) {
      const root = data && typeof data === 'object' ? data : {}
      return {
        remainingUnits: this.numberValue(root.remainingUnits, 0),
        totalPurchased: this.numberValue(root.totalPurchased, 0),
        totalUsed: this.numberValue(root.totalUsed, 0),
        unitCost: this.numberValue(root.unitCost, 0.354),
        totalAmountRecharged: this.numberValue(root.totalAmountRecharged, 0),
        lastRecharge: root.lastRecharge && typeof root.lastRecharge === 'object' ? root.lastRecharge : null
      }
    },
    fetchQuota () {
      this.quotaLoading = true
      return axios.get(`${store.state.baseURL}v1/admin/messages/quota`, {
        headers: { authorization: store.state.token }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'Could not load the SMS balance.')
        this.quota = this.normalizeQuota(body.data)
      }).catch(err => {
        this.notify('error', 'SMS balance', this.errorMessage(err))
      }).finally(() => {
        this.quotaLoading = false
      })
    },
    fetchHistory () {
      this.historyLoading = true
      this.historyError = ''
      return axios.get(`${store.state.baseURL}v1/admin/messages/quota/recharges`, {
        headers: { authorization: store.state.token },
        params: { page: this.historyPage, limit: this.historyLimit }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'Could not load the recharge history.')
        const data = body.data && typeof body.data === 'object' ? body.data : {}
        const rows = Array.isArray(data.rows) ? data.rows : []
        this.historyRows = rows.map((row, index) => ({
          id: `${row.createdAt || 'row'}-${index}`,
          amount: this.numberValue(row.amount, 0),
          unitsAdded: this.numberValue(row.unitsAdded, 0),
          remark: row.remark || '',
          createdAt: row.createdAt
        }))
        this.historyTotal = this.numberValue(data.total, this.historyRows.length)
        this.historyPage = Math.max(1, this.numberValue(data.page, this.historyPage))
        this.historyLimit = Math.max(1, this.numberValue(data.limit, this.historyLimit))
        this.historyTotalPages = Math.max(1, this.numberValue(data.totalPage, Math.ceil(this.historyTotal / this.historyLimit)))
      }).catch(err => {
        this.historyRows = []
        this.historyTotal = 0
        this.historyTotalPages = 1
        this.historyError = this.errorMessage(err)
      }).finally(() => {
        this.historyLoading = false
      })
    },
    goToHistoryPage (page) {
      if (page < 1 || page > this.historyTotalPages || page === this.historyPage) return
      this.historyPage = page
      this.fetchHistory()
    },
    submitRecharge () {
      const amount = Number(this.rechargeForm.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        this.notify('error', 'Invalid amount', 'Enter a recharge amount greater than zero.')
        return
      }
      this.rechargeSubmitting = true
      return axios.post(`${store.state.baseURL}v1/admin/messages/quota/recharge`, {
        amount, remark: this.rechargeForm.remark
      }, {
        headers: { authorization: store.state.token }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'The recharge could not be completed.')
        this.quota = this.normalizeQuota(body.data)
        this.rechargeForm = { amount: null, remark: '' }
        this.notify('success', 'Recharge successful', `Added ${this.formatNumber(this.quota.lastRecharge ? this.quota.lastRecharge.unitsAdded : 0)} units to the SMS balance.`)
        this.historyPage = 1
        this.fetchHistory()
      }).catch(err => {
        this.notify('error', 'Recharge failed', this.errorMessage(err))
      }).finally(() => {
        this.rechargeSubmitting = false
      })
    }
  }
}
</script>

<style scoped>
.sms-recharge { --forest:#173f35; --forest-2:#245c4d; --green:#2e7d5b; --paper:#fffefa; --mist:#f5f7f4; --red:#b44343; --ink:#26332f; max-width:900px; padding:20px; color:var(--ink); }
.recharge-header { margin-bottom:20px; padding:24px 26px; border-left:5px solid var(--green); background:var(--paper); box-shadow:0 5px 18px rgba(23,63,53,.07); }
.eyebrow { margin-bottom:4px; color:var(--green); font-size:11px; font-weight:800; letter-spacing:.13em; text-transform:uppercase; }
.recharge-header h1 { margin:0 0 7px; color:var(--forest); font-size:26px; font-weight:700; letter-spacing:-.025em; }
.recharge-header p { max-width:560px; margin:0; color:#60706a; font-size:13px; }
.balance-panel,.recharge-panel,.history-panel { margin-bottom:16px; border:1px solid #d9e2dd; border-radius:4px; background:var(--paper); box-shadow:0 4px 16px rgba(38,51,47,.045); }
.balance-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:14px; padding:18px; }
.balance-cell { display:flex; flex-direction:column; gap:3px; }
.balance-cell span { color:#64736d; font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; }
.balance-cell strong { color:var(--forest); font-size:22px; font-weight:700; line-height:1.2; }
.balance-cell small { color:#78857f; font-size:11px; }
.unit-label { color:#78857f; font-size:12px; font-weight:600; text-transform:none; letter-spacing:0; }
.recharge-panel h2 { margin:0; padding:16px 18px; border-bottom:1px solid #e3e9e5; color:var(--forest); font-size:16px; font-weight:700; }
.recharge-form { display:flex; flex-direction:column; gap:14px; padding:18px; }
.field label { display:block; margin-bottom:5px; color:#53625c; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.035em; }
.field input { box-sizing:border-box; width:100%; height:39px; padding:7px 9px; border:1px solid #cad6d0; border-radius:3px; background:#fff; color:var(--ink); font-size:13px; }
.field input:focus { outline:3px solid rgba(46,125,91,.18); outline-offset:1px; }
.field-hint { display:block; margin-top:5px; color:#78857f; font-size:11px; }
.recharge-actions { display:flex; justify-content:flex-end; }
.btn-ledger { min-height:38px; padding:8px 16px; border:1px solid transparent; border-radius:3px; font-size:12px; font-weight:700; transition:background .15s ease,transform .15s ease,box-shadow .15s ease; }
.btn-ledger:not(:disabled):hover { transform:translateY(-1px); box-shadow:0 3px 8px rgba(23,63,53,.15); }
.btn-ledger:disabled { cursor:not-allowed; opacity:.52; }
.btn-apply { background:var(--forest-2); color:#fff; }
.btn-apply:hover { background:var(--forest); }
.button-spinner { display:inline-block; width:13px; height:13px; margin-right:4px; vertical-align:-2px; border:2px solid rgba(255,255,255,.45); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
@media(max-width:640px){.balance-grid{grid-template-columns:1fr}}
@media(prefers-reduced-motion:reduce){.button-spinner{animation:none}.btn-ledger{transition:none}}
.history-heading{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:16px 18px;border-bottom:1px solid #e3e9e5}
.history-heading h2{margin:0 0 2px;color:var(--forest);font-size:16px;font-weight:700}.history-heading p{margin:0;color:#75827d;font-size:12px}
.table-scroll{overflow-x:auto}table{width:100%;min-width:520px;border-collapse:collapse}th{padding:10px 13px;border-bottom:2px solid #c9d8d0;background:var(--mist);color:#53645d;font-size:10px;font-weight:800;letter-spacing:.055em;text-align:left;text-transform:uppercase}td{padding:12px 13px;border-bottom:1px solid #e5eae7;color:#35423d;font-size:12px;vertical-align:top}tbody tr:last-child td{border-bottom:0}.number-cell{text-align:right;font-variant-numeric:tabular-nums}
.state-panel{display:flex;min-height:160px;align-items:center;justify-content:center;flex-direction:column;padding:30px;text-align:center;color:#708079}.state-panel strong{color:var(--forest);font-size:14px}.state-panel p{max-width:420px;margin:5px 0 12px;font-size:12px}.state-error strong{color:var(--red)}
.ledger-loader{width:24px;height:24px;margin-bottom:10px;border:2px solid #d2ded8;border-top-color:var(--green);border-radius:50%;animation:spin .7s linear infinite}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #e1e8e4;background:#fbfcfa;color:#64726c;font-size:12px}.pagination-bar nav{display:flex;align-items:center;gap:10px}.pagination-bar button{display:grid;width:28px;height:28px;place-items:center;border:1px solid #c8d5cf;border-radius:3px;background:#fff;color:var(--forest-2)}.pagination-bar button:disabled{opacity:.35}.pagination-bar button:not(:disabled):hover{background:#eaf2ee}
@media(max-width:640px){.history-heading{align-items:flex-start;flex-direction:column}}
</style>
